import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} content
 * @property {HTMLButtonElement} toggleButton
 * @property {HTMLElement} toggleLabel
 */

/**
 * Clamped product description with animated View more / View less toggle.
 *
 * Collapsed state uses CSS `-webkit-line-clamp` + `max-height` so the ellipsis
 * renders correctly. To animate, we hold the element in the no-clamp (expanded)
 * state for the duration of the transition and only re-engage the clamp at
 * `transitionend`. Otherwise applying line-clamp would snap the box height
 * immediately and bypass the max-height transition.
 *
 * @extends {Component<Refs>}
 */
class ProductDescriptionToggle extends Component {
  requiredRefs = ['content', 'toggleButton', 'toggleLabel'];

  #resizeObserver;
  /** @type {number | null} */
  #collapsedHeight = null;

  connectedCallback() {
    super.connectedCallback();

    // Cache the clamped height while we know the element is collapsed, so we
    // have a target value to animate to when collapsing later (we can't read
    // it once line-clamp is unset).
    requestAnimationFrame(() => {
      this.#cacheCollapsedHeight();
      this.#syncToggleVisibility();
    });

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.dataset.state !== 'expanded') this.#cacheCollapsedHeight();
      this.#syncToggleVisibility();
    });
    this.#resizeObserver.observe(this.refs.content);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  toggle = () => {
    if (this.dataset.state === 'expanded') {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  #expand() {
    const { content, toggleButton, toggleLabel } = this.refs;

    // Pin to current clamped height so the transition has a defined start.
    const startHeight = content.clientHeight;
    content.style.maxHeight = `${startHeight}px`;

    // Switch state now (drops line-clamp). The pinned max-height keeps the
    // box at startHeight until we change it below.
    this.dataset.state = 'expanded';

    // Force layout to commit the start frame before changing to the target.
    void content.offsetHeight;

    const target = content.scrollHeight;
    content.style.maxHeight = `${target}px`;

    content.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName !== 'max-height') return;
        if (this.dataset.state === 'expanded') content.style.maxHeight = 'none';
      },
      { once: true }
    );

    toggleButton.setAttribute('aria-expanded', 'true');
    toggleLabel.textContent = 'View less';
  }

  #collapse() {
    const { content, toggleButton, toggleLabel } = this.refs;

    // Pin to current expanded height (max-height was 'none', which isn't
    // interpolatable). Stay in 'expanded' state so line-clamp doesn't snap
    // the box height before the animation runs.
    content.style.maxHeight = `${content.scrollHeight}px`;
    void content.offsetHeight;

    const target = this.#collapsedHeight ?? this.#fallbackCollapsedHeight();
    content.style.maxHeight = `${target}px`;

    content.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName !== 'max-height') return;
        // Re-engage line-clamp + ellipsis, hand max-height back to CSS.
        this.dataset.state = 'collapsed';
        content.style.maxHeight = '';
      },
      { once: true }
    );

    toggleButton.setAttribute('aria-expanded', 'false');
    toggleLabel.textContent = 'View more';
  }

  #cacheCollapsedHeight() {
    // Only valid while the element is actually clamped.
    if (this.dataset.state === 'expanded') return;
    this.#collapsedHeight = this.refs.content.clientHeight;
  }

  #fallbackCollapsedHeight() {
    const lh = parseFloat(getComputedStyle(this.refs.content).lineHeight) || 24;
    const lines = parseInt(getComputedStyle(this).getPropertyValue('--clamp-lines'), 10) || 3;
    return lh * lines;
  }

  #syncToggleVisibility() {
    const { content, toggleButton } = this.refs;
    if (this.dataset.state === 'expanded') return;
    const overflows = content.scrollHeight - content.clientHeight > 1;
    toggleButton.hidden = !overflows;
  }
}

if (!customElements.get('product-description-toggle')) {
  customElements.define('product-description-toggle', ProductDescriptionToggle);
}
